import { LineChartAxisSide, type LineChartAxis, type LineChartSeries } from '@shigo-ui/viz';

import { reportMetricDefinitions } from '../reportExplorer/catalog';
import {
  formatReportChartAxisValue,
  formatReportMetricValue,
  numericReportValue,
  reportMetricLabel,
} from '../reportExplorer/format';
import type { ReportResponse } from '../reportExplorer/model';
import { ReportMetric } from '../reportExplorer/reportEnums';
import type { PerformanceEntityChartConfiguration, PerformanceEntityOption } from './model';

const colors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
];

export function performanceEntityOptions(
  response: ReportResponse | undefined,
  identityField: string,
  labelField: string,
  rankMetric = ReportMetric.Revenue,
  limit = 10,
): PerformanceEntityOption[] {
  const totals = new Map<string, { label: string; value: number }>();
  for (const row of response?.report ?? []) {
    const id = stringValue(row[identityField]);
    if (!id) continue;
    const current = totals.get(id) ?? { label: stringValue(row[labelField]) ?? id, value: 0 };
    current.value += numericReportValue(row[reportMetricDefinitions[rankMetric].field]) ?? 0;
    totals.set(id, current);
  }
  return [...totals.entries()]
    .sort((left, right) => right[1].value - left[1].value)
    .slice(0, limit)
    .map(([id, item]) => ({ id, label: item.label }));
}

export function projectEntityPerformanceChart(
  response: ReportResponse | undefined,
  comparison: ReportResponse | undefined,
  entities: readonly PerformanceEntityOption[],
  config: PerformanceEntityChartConfiguration,
) {
  const axes: LineChartAxis[] = config.metrics.map((metric, index) => ({
    id: `performance-${metric}`,
    label: reportMetricLabel(metric),
    color: chartColor(index),
    side: index % 2 === 0 ? LineChartAxisSide.Left : LineChartAxisSide.Right,
    axisValueFormatter: (value) => formatReportChartAxisValue(value, metric),
    valueFormatter: (value) => formatReportMetricValue(value, metric),
  }));
  const series = config.metrics.flatMap((metric, metricIndex) =>
    entities.flatMap((entity, entityIndex): LineChartSeries[] => {
      const base = entitySeries(response, entity, metric, config, metricIndex, entityIndex, false);
      if (!comparison) return [base];
      return [
        base,
        entitySeries(comparison, entity, metric, config, metricIndex, entityIndex, true),
      ];
    }),
  );
  return { axes, series };
}

function entitySeries(
  response: ReportResponse | undefined,
  entity: PerformanceEntityOption,
  metric: ReportMetric,
  config: PerformanceEntityChartConfiguration,
  metricIndex: number,
  entityIndex: number,
  dashed: boolean,
): LineChartSeries {
  const rows = (response?.report ?? []).filter(
    (row) => stringValue(row[config.identityField]) === entity.id,
  );
  return {
    id: `${entity.id}:${metric}${dashed ? ':comparison' : ''}`,
    label: `${entity.label} (${reportMetricLabel(metric)})`,
    legendGroupId: `${entity.id}:${metric}`,
    color: chartColor(entityIndex + metricIndex),
    axisId: `performance-${metric}`,
    dashed,
    data: rows.flatMap((row, index) => {
      const value = numericReportValue(row[reportMetricDefinitions[metric].field]);
      if (value === undefined) return [];
      return [{ x: index, label: pointLabel(row), value }];
    }),
  };
}

function pointLabel(row: ReportResponse['report'][number]) {
  const day = stringValue(row.day);
  const hour = stringValue(row.hour);
  return [day, hour].filter(Boolean).join(' ') || '-';
}

function stringValue(value: unknown) {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : undefined;
}

function chartColor(index: number) {
  return colors[index % colors.length] ?? 'var(--foreground)';
}
